<template>
  <div class="wrapper">
    <div class="avatar">
      <AuthorAvatar
        :author="author"
        size="small"
      />
    </div>
    <div class="content">
      <div
        class="author-age"
        @click="onUserClick(author)"
      >
        {{ author.name }} - {{ postAge }}
      </div>
      <div class="reply">
        <ContentEditor
          ref="replyEditor"
          v-model="replyContent"
          :default-content-json="replyData.content"
          :editable="isEditable"
          class="reply-content-view"
          @dblclick.native="onEditReply"
        />
        <ForumReplyButtonSet
          v-show="isEditable"
          is-edit
          @onSubmit="updateReply"
          @onCancel="cancelReplyEditor"
        />
        <p
          v-show="replyUpdatedDate"
          class="updated-date"
        >
          Edited: {{ replyUpdatedDate }}
        </p>
        <PreviewContainer
          :files="replyFiles"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ContentEditor from './ContentEditor'
import AuthorAvatar from './AuthorAvatar'
import PreviewContainer from '../dropzone/Preview-Container'
import ForumReplyButtonSet from './ForumReplyButtonSet'
import { saveAndReplaceImagesWithinReply, isContentEmpty } from '@/helpers/forum'
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment'

const DEFAULT_EDITOR_CONTENT = {
  JSON: {
    content: {}
  },
  HTML: '',
  IMAGES: null,
  ATTACHMENTS: null
}

export default {
  components: {
    ContentEditor,
    AuthorAvatar,
    PreviewContainer,
    ForumReplyButtonSet
  },

  props: {
    replyData: {
      type: Object,
      default: () => {}
    },
    users: {
      type: Array,
      default: () => []
    },
    userId: Number,
    parentReplyMarkup: String
  },

  data () {
    return {
      loadingChildReplies: true,
      showReplyEditor: false,
      childReplies: [],
      replyFiles: [],
      replyContent: _.cloneDeep(DEFAULT_EDITOR_CONTENT),
      isEditable: false,
      postId: null
    }
  },

  computed: {
    author () {
      return this.users.find(user => user.id === this.replyData.authorId) || {}
    },

    postAge () {
      const currentDate = moment()
      const createdDate = moment(this.replyData.createdAt)

      if (currentDate.diff(createdDate, 'days') > 1) {
        const date = moment(createdDate).format('YYYY-MM-DD')
        const time = moment(createdDate).format('hh:mm a')
        return `${date} at ${time}`
      }
      return moment(this.replyData.createdAt).fromNow()
    },

    isReplyEditable () {
      return this.userId === this.replyData.authorId
    },

    replyUpdatedDate () {
      // If there's no replyData info or the Reply creation date is the same than the updated date, then don't show the last updated info (return an empty string).
      if (!this.replyData || this.replyData.createdAt === this.replyData.updatedAt) {
        return ''
      }

      // Get the Post updated date.
      const updatedDate = moment(this.replyData.updatedAt)

      // Get the current date and extract the updated date and time.
      const currentDate = moment()
      const date = moment(updatedDate).format('YYYY-MM-DD')
      const time = moment(updatedDate).format('hh:mm a')

      // If it has been a week or more since last update, then show the date and time. Otherwise, show the elapsed time.
      return currentDate.diff(updatedDate, 'week') >= 1 ? `${date} at ${time}` : moment(updatedDate).fromNow()
    },

    embeddedImages () {
      return this.replyContent.IMAGES || []
    }
  },

  created () {
    this.postId = this.$route.params.id
  },

  methods: {
    /**
     * Makes editable the post content.
     */
    onEditReply () {
      // If the current user is the Reply author, make the Content Editor editable.
      if (this.isReplyEditable) {
        this.isEditable = true
      }
    },

    /**
     * Cancels the edition/update of the post.
     */
    cancelReplyEditor () {
      // Set the original Reply content to the Content Editor.
      this.$set(this.replyContent, 'JSON', JSON.parse(this.replyData.content))
      this.$refs.replyEditor.editor.setContent(this.replyContent.JSON)

      // Make the Content Editor non-editable (readonly).
      this.isEditable = false
    },

    /**
     * Updates the content of the post.
     */
    async updateReply () {
      try {
        if (this.embeddedImages.length) {
          const jsonContent = this.replyContent.JSON.content
          this.replyContent.JSON.content = await saveAndReplaceImagesWithinReply(jsonContent, this.embeddedImages)
        }

        const reply = {
          replyId: this.replyData.id,
          content: this.replyContent.JSON,
          markup: this.replyContent.HTML,
          authorName: this.$auth.getUser().name,
          authorId: this.$auth.getUser().id,
          parentPostId: this.postId
        }

        if (isContentEmpty(reply.markup)) {
          this.$Message.error('Content cannot empty.')
          return
        }

        await axios.put(`${process.env.VUE_APP_API_URI}/forum/reply/${this.replyData.id}`, { reply, context: this.parentReplyMarkup })
        this.isEditable = false
      } catch (error) {
        this.$Message.error(`Failed to update the reply: ${error}`)
      }
    },

    /**
     * Route to forum user profile
     */
    onUserClick (author) {
      this.$router.replace({
        name: 'Forum User',
        query: {
          userId: author.id
        }
      })
    }
  }
}
</script>

/<style scoped>
.wrapper {
  margin: 8px 0;
  padding-left: 8px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  border-left: 8px solid var(--color-gray-button);
}

.avatar {
  width: 24px;
  height: 24px;
}

.content {
  width: calc(100% - 24px);
}

.author-age {
  cursor: pointer;
}

.content .author-age {
  padding-left: 8px;
  font-weight: bold;
}

.content .reply {
  margin: 0;
  padding: 0;
}

.content .updated-date {
  margin: 0;
  padding-right: 8px;
  padding-bottom: 8px;
  font-style: italic;
  text-align: end;
  color: var(--color-mid-gray);
}

</style>
